import { Component, OnInit, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as Highcharts from 'highcharts';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { MeetingDetailDialogComponent } from '../../scheduling/meeting-detail-dialog.component';
import { ViewTaskModalComponent } from '../../tasks/view-task-modal/view-task-modal.component';
import { Location } from '@angular/common'; // 1. Import Location
import { NewMeetingSchedulerComponent } from '../../scheduling/new-meeting-scheduler/new-meeting-scheduler.component';
import { CreateNewTaskComponent } from '../../tasks/create-new-task/create-new-task.component';
import { GoalDetailsComponent } from '../../goals/goal-details/goal-details.component';

export interface Meeting {
  meetingId: number;
  meetingSubject: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  joinUrl: string;
  createdUserName: string;
  hospitalName: string;
  attendeesNames: string[];
  duration: string;
  timeZone: string;
  isRecurring?: boolean;
}

interface GoalOpsStatusPercent {
  ON_TRACK: number;
  AT_RISK: number;
  OFF_TRACK: any;
}

interface GoalOpsItem {
  goalId: number;
  goalName: string;
  statusPercent: GoalOpsStatusPercent;
}

@Component({
  selector: 'vc-ops-dashboard',
  templateUrl: './ops-dashboard.component.html',
  styleUrl: './ops-dashboard.component.scss',
  standalone: false
})
export class OPSDashboardComponent implements OnInit {
  hospitalSearchTerm: string = '';
  filteredHospitalNames: string[] = [];

  // Highcharts configuration
  chartOptions: Highcharts.Options = {};

  // Track which row is currently expanded (-1 means none)
  public expandedHospitalId: number = -1;

  // Sorting
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;

  // Filters
  filterHospitals: string[] = [];
  filterRMs: string[] = [];
  filterGoals: string[] = [];
  filterLeadershipAttentionRequiredList: string[] = [];
  priorityPracticeOnly: boolean = false;
  viewMode: 'all' | 'hospital' = 'all';
  leadershipFilterOptions = [
    { key: 'NO_ATTENTION_NEEDED', label: 'No Attention Needed' },
    { key: 'MONITOR', label: 'Monitor' },
    { key: 'ATTENTION_REQUIRED', label: 'Attention Required' },
  ];

  quarterList: any[] = [];
  selectedQuarter: string = '';

  taskList: any[] = [];
  pastDueTasks: any[] = [];
  upcomingTasks: any[] = [];
  taskStats: any[] = [];

  isLoadingPerformance: boolean = true;
  isLoadingHospitals: boolean = true;
  isLoadingTasks: boolean = true;
  isLoadingMeetings: boolean = true;

  nextMeeting: Meeting | null = null;
  remainingMeetings: Meeting[] = [];
  allMeetings: any;
  hospitalList: any;
  OverallPerformance: any;
  dashboardDataList: any[] = [];
  goalOps: GoalOpsItem[] = [];
  currentQtr: any;


  constructor(public router: Router, public location: Location,
    private dialog: MatDialog,
    private leaderService: LeaderService,
  ) {
  }

  ngOnInit(): void {
    // Initialize component
    this.opsEventUpdateEvent();
    this.qtrList();
    this.loadTodayMeetings();
    this.getHospitalList();
    this.filteredHospitalNames = this.uniqueHospitalNames;
  }

  opsEventUpdateEvent() {
    this.leaderService.opsEventUpdate.subscribe((data: any) => {
      console.log('Received Ops event update:', data);
      if (data) {
        this.applyQtr(this.quarterList)
      }
    });
  }

  get filteredData(): any[] {
    if (!this.dashboardDataList) return [];
    const selectedGoals = (this.filterGoals || []).map((g: any) => this.normalizeGoalName(g));
    return this.dashboardDataList.filter(h => {
      const matchHospital = !this.filterHospitals?.length || this.filterHospitals.includes(h.hospitalName);
      const matchRM = !this.filterRMs?.length || this.filterRMs.includes(h.rmName);
      const matchLeadership =
        !this.filterLeadershipAttentionRequiredList?.length
        || this.filterLeadershipAttentionRequiredList.includes(this.normalizeLeadershipAttentionRequiredKey(h.leadershipAttentionRequired));
      const matchPriorityPractice = !this.priorityPracticeOnly || h.isCritical === true;
      const goals = Array.isArray(h?.goals) ? h.goals : [];
      const matchGoal = selectedGoals.length === 0 || goals.some((g: any) => selectedGoals.includes(this.normalizeGoalName(g?.goalName)));
      return matchHospital && matchRM && matchLeadership && matchPriorityPractice && matchGoal;
    });
  }

  getFilteredHospitalGoals(hospital: any): any[] {
    const goals = Array.isArray(hospital?.goals) ? hospital.goals : [];
    const selectedGoals = (this.filterGoals || []).map((g: any) => this.normalizeGoalName(g));

    if (!selectedGoals.length) {
      return goals;
    }

    return goals.filter((goal: any) => selectedGoals.includes(this.normalizeGoalName(goal?.goalName)));
  }

  hasGoalFilter(): boolean {
    return !!(this.filterGoals?.length);
  }

  getEffectiveGoals(hospital: any): any[] {
    return this.hasGoalFilter() ? this.getFilteredHospitalGoals(hospital) : (hospital.goals || []);
  }

  getEffectiveOpsScore(hospital: any): number | null {
    if (!this.hasGoalFilter()) return hospital.opsScore;
    const goals = this.getEffectiveGoals(hospital);
    const goalsWithOps = goals.filter((g: any) => g.opsScore != null && g.opsScore !== undefined);
    if (!goalsWithOps.length) return null;
    return goalsWithOps.reduce((sum: number, g: any) => sum + g.opsScore, 0) / goalsWithOps.length;
  }

  getEffectiveStatus(hospital: any): string {
    if (!this.hasGoalFilter()) return hospital.status;
    const goals = this.getEffectiveGoals(hospital);
    if (!goals.length) return hospital.status;

    const statusPriority: Record<string, number> = { 'ON_TRACK': 1, 'At_Risk': 2, 'Off_Track': 3 };
    let worstStatus = '';
    let maxPriority = 0;

    for (const g of goals) {
      const priority = statusPriority[g.status] || 0;
      if (priority > maxPriority) {
        maxPriority = priority;
        worstStatus = g.status;
      }
    }

    return worstStatus || hospital.status;
  }

  getEffectiveMomentum(hospital: any): string | null {
    if (!this.hasGoalFilter()) return hospital.momentum;
    const goals = this.getEffectiveGoals(hospital);
    if (!goals.length) return hospital.momentum;

    const momentumPriority: Record<string, number> = { 'IMPROVING': 1, 'FLAT': 2, 'DECLINING': 3 };
    let worstMomentum: string | null = null;
    let maxPriority = 0;

    for (const g of goals) {
      if (!g.momentum) continue;
      const priority = momentumPriority[g.momentum] || 0;
      if (priority > maxPriority) {
        maxPriority = priority;
        worstMomentum = g.momentum;
      }
    }

    return worstMomentum ?? hospital.momentum;
  }

  getEffectiveExecutionHealth(hospital: any): string {
    if (!this.hasGoalFilter()) return hospital.executionHealth;
    const goals = this.getEffectiveGoals(hospital);
    if (!goals.length) return hospital.executionHealth;

    const healthPriority: Record<string, number> = { 'ACTIVE': 1, 'SLOWING': 2, 'STALLED': 3 };
    let worstHealth = '';
    let maxPriority = 0;

    for (const g of goals) {
      const normalized = (g.executionHealth || '').toString().toUpperCase().replace(/[_-]+/g, ' ');
      let level = '';
      if (normalized.includes('ACTIVE') || normalized.includes('GREEN')) level = 'ACTIVE';
      else if (normalized.includes('SLOWING') || normalized.includes('YELLOW')) level = 'SLOWING';
      else if (normalized.includes('STALLED') || normalized.includes('RED')) level = 'STALLED';

      const priority = healthPriority[level] || 0;
      if (priority > maxPriority) {
        maxPriority = priority;
        worstHealth = g.executionHealth;
      }
    }

    return worstHealth || hospital.executionHealth;
  }

  get uniqueHospitalNames(): string[] {
    return Array.from(new Set((this.dashboardDataList || []).map(h => h.hospitalName))).sort();
  }

  get uniqueRMNames(): string[] {
    return Array.from(new Set((this.dashboardDataList || []).map(h => h.rmName))).sort();
  }

  get uniqueGoalNames(): string[] {
    const goalNameMap: Map<string, string> = (this.dashboardDataList || []).reduce((map: Map<string, string>, hospital: any) => {
      const goals = Array.isArray(hospital?.goals) ? hospital.goals : [];
      goals.forEach((goal: any) => {
        const displayGoalName = this.normalizeGoalName(goal?.goalName, false);
        const normalizedGoalName = this.normalizeGoalName(displayGoalName);

        if (normalizedGoalName && !map.has(normalizedGoalName)) {
          map.set(normalizedGoalName, displayGoalName);
        }
      });
      return map;
    }, new Map<string, string>());

    return Array.from(goalNameMap.values()).sort((a: string, b: string) => a.localeCompare(b));
  }

  get uniqueLeadershipFlags(): string[] {
    const keys = new Set(
      (this.dashboardDataList || [])
        .map((h: any) => this.normalizeLeadershipAttentionRequiredKey(h?.leadershipAttentionRequired))
        .filter((value: any) => !!value)
    );
    return this.leadershipFilterOptions
      .filter(option => keys.has(option.key))
      .map(option => option.key);
  }

  getLeadershipFilterLabel(key: string): string {
    return this.leadershipFilterOptions.find(option => option.key === key)?.label || key;
  }

  togglePriorityPracticeFilter(): void {
    this.priorityPracticeOnly = !this.priorityPracticeOnly;
    this.applyTableFilters();
  }

  getLeadershipAttentionLabel(value: any): string {
    return this.normalizeLeadershipAttentionRequired(value) || '-';
  }

  getLeadershipAttentionClass(value: any): string {
    const key = this.normalizeLeadershipAttentionRequiredKey(value);
    if (key === 'ATTENTION_REQUIRED') return 'attention-required';
    if (key === 'MONITOR') return 'monitor';
    if (key === 'NO_ATTENTION_NEEDED') return 'no-attention-needed';
    return '';
  }

  private normalizeLeadershipAttentionRequired(value: any): string {
    const normalized = (value || '').toString().trim().toUpperCase().replace(/\s+/g, ' ');
    if (normalized === 'ATTENTION REQUIRED') return 'Attention Required';
    if (normalized === 'MONITOR') return 'Monitor';
    if (normalized === 'NO ATTENTION NEEDED' || normalized === 'NO ATTENSION REQUIRED' || normalized === 'NO ATTENTION REQUIRED') {
      return 'No Attention Needed';
    }
    return '';
  }

  private normalizeLeadershipAttentionRequiredKey(value: any): string {
    const label = this.normalizeLeadershipAttentionRequired(value);
    if (label === 'Attention Required') return 'ATTENTION_REQUIRED';
    if (label === 'Monitor') return 'MONITOR';
    if (label === 'No Attention Needed') return 'NO_ATTENTION_NEEDED';
    return '';
  }

  private normalizeGoalName(goalName: any, forComparison: boolean = true): string {
    const normalized = (goalName || '')
      .toString()
      .normalize('NFKC')
      .replace(/\u00A0/g, ' ')
      .replace(/[‐‑‒–—―−]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();

    return forComparison ? normalized.toLowerCase() : normalized;
  }

  applyTableFilters(): void {
    this.currentPage = 1;
    this.expandedHospitalId = -1;
  }

  resetTableFilters(): void {
    this.filterHospitals = [];
    this.filterRMs = [];
    this.filterGoals = [];
    this.filterLeadershipAttentionRequiredList = [];
    this.priorityPracticeOnly = false;
    this.viewMode = 'all';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.currentPage = 1;
    this.expandedHospitalId = -1;
  }

  get sortedData(): any[] {
    if (!this.filteredData) return [];
    if (!this.sortColumn) return this.filteredData;
    return [...this.filteredData].sort((a, b) => {
      const valA = a[this.sortColumn] ?? '';
      const valB = b[this.sortColumn] ?? '';
      const cmp = typeof valA === 'number'
        ? valA - valB
        : String(valA).localeCompare(String(valB));
      return this.sortDirection === 'asc' ? cmp : -cmp;
    });
  }

  get totalPages(): number {
    return Math.ceil(this.sortedData.length / this.pageSize) || 1;
  }

  get pagedData(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    let start = Math.max(1, current - 1);
    let end = Math.min(total, start + 2);
    // adjust start if we're near the end
    if (end - start < 2) {
      start = Math.max(1, end - 2);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  get paginationInfo(): string {
    const total = this.sortedData.length;
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, total);
    return `Showing ${start}-${end} of ${total} hospitals`;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return '↕';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.expandedHospitalId = -1;
    }
  }
  qtrList() {
    this.leaderService.getQtrList().subscribe((res: any) => {
      this.quarterList = res?.data || [];
      this.selectedQuarter = this.getCurrentQuarterName();
      this.applyQtr(this.quarterList);
    });
  }

  private getCurrentQuarterName(): string {
    const currentQuarter = this.quarterList.find((q: any) => q?.currentQuarter);
    return currentQuarter?.quarterName || 'default';
  }

  private getResolvedQuarter(quarter?: any): string {
    return quarter || this.selectedQuarter || this.getCurrentQuarterName();
  }

  getOpsHospitalData(qtr?: any) {
    const resolvedQuarter = this.getResolvedQuarter(qtr);
    this.isLoadingHospitals = true;
    this.leaderService.getOpsHospitalData(resolvedQuarter).subscribe({
      next: (res: any) => {
        this.isLoadingHospitals = false;
        if (res && res.data) {
          this.dashboardDataList = (res.data || []).map((hospital: any) => ({
            ...hospital,
            rmAttentionRequired: hospital?.rmActionRequired,
            leadershipAttentionRequired: this.normalizeLeadershipAttentionRequired(hospital?.leadershipAttentionRequired),
            isCritical: hospital?.isCritical === true,
          }));
          this.filteredHospitalNames = this.uniqueHospitalNames;
        }
      },
      error: (err) => {
        this.isLoadingHospitals = false;
      }
    });
  }
  filterHospitalOptions(): void {
    const term = this.hospitalSearchTerm.toLowerCase();
    this.filteredHospitalNames = this.uniqueHospitalNames.filter(h => h.toLowerCase().includes(term));
  }

  getOpsStatus(quarter?: any) {
    const resolvedQuarter = this.getResolvedQuarter(quarter);
    this.isLoadingPerformance = true;
    this.leaderService.getOpsStatus(resolvedQuarter).subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.OverallPerformance = res.data;
          this.isLoadingPerformance = false;
          const goalOpsData = this.OverallPerformance?.goalOps;
          if (Array.isArray(goalOpsData)) {
            this.goalOps = goalOpsData;
          } else if (this.OverallPerformance?.statusPercent) {
            this.goalOps = [{
              goalId: 0,
              goalName: 'Revenue at Risk',
              statusPercent: this.OverallPerformance.statusPercent
            }];
          } else {
            this.goalOps = [];
          }
        }
      },
      error: (err) => {
        this.isLoadingPerformance = false;
        console.error('Error fetching Ops status:', err);
      }
    });
  }

  applyQtr(quarterList: any[]): void {
    const resolvedQuarter = this.getResolvedQuarter(this.selectedQuarter);
    this.selectedQuarter = resolvedQuarter;
    this.getOpsStatus(resolvedQuarter);
    this.getOpsHospitalData(resolvedQuarter);
    let currentQtrLst = quarterList.find((q: any) => q.quarterName === resolvedQuarter) || null;
    this.currentQtr = currentQtrLst ? currentQtrLst.currentQuarter : null;
    // Always load tasks for the selected quarter (current, past, or future)
    this.loadTaskList(resolvedQuarter);
    this.loadTodayMeetings();
  }

  loadTaskList(quarter: any): void {
    this.isLoadingTasks = true;
    this.leaderService.getTaskList(quarter).subscribe({
      next: (response: any) => {
        this.isLoadingTasks = false;
        if (response && response.data) {
          this.updateStats(response.data);
          this.taskList = response.data.tasksDetails || [];
          this.separateTasks();
        }
      },
      error: (error) => {
        this.isLoadingTasks = false;
        this.taskList = [];
        this.pastDueTasks = [];
        this.upcomingTasks = [];
        this.taskStats = [];
      }
    });
  }

  separateTasks(): void {
    this.pastDueTasks = this.taskList.filter(task => task.statusName && task.statusName.toLowerCase() === 'past due');
    this.upcomingTasks = this.taskList
      .filter(task => task.statusName && task.statusName.toLowerCase() === 'open')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }


  updateStats(data: any): void {
    this.taskStats = [
      {
        count: data.pastDueTasks || 0,
        label: 'Past Due\nTasks',
        cssClass: 'past-due'
      },
      {
        count: data.assignedToMe || 0,
        label: 'Tasks Assigned\nto Me',
        cssClass: 'assigned-me'
      },
      {
        count: data.assignedToOthers || 0,
        label: 'Tasks Assigned\nto Others',
        cssClass: 'assigned-others'
      },
      {
        count: data.completedTasks || 0,
        label: 'Completed\nTasks',
        cssClass: 'completed'
      },
    ];
  }

  getStatusClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s.includes('past due')) return 'past-due';
    if (s.includes('complet')) return 'complete';
    return 'pending';
  }

  toggleRow(hospitalId: number): void {
    this.expandedHospitalId = this.expandedHospitalId === hospitalId ? -1 : hospitalId;
  }

  isRowExpanded(hospitalId: number): boolean {
    return this.expandedHospitalId === hospitalId;
  }

  private getMeetingDateTime(dateValue: string, timeValue: string): Date {
    const [year, month, day] = (dateValue || '').split('-').map((value) => Number(value));
    const [hours = 0, minutes = 0, seconds = 0] = (timeValue || '00:00:00')
      .split(':')
      .map((value) => Number(value));

    return new Date(year || 0, (month || 1) - 1, day || 1, hours, minutes, seconds, 0);
  }

  private getMeetingStartDateTime(meeting: Meeting): Date {
    return this.getMeetingDateTime(meeting?.scheduledDate, meeting?.startTime);
  }

  private getMeetingEndDateTime(meeting: Meeting): Date {
    return this.getMeetingDateTime(meeting?.scheduledDate, meeting?.endTime);
  }

  loadTodayMeetings(): void {
    this.isLoadingMeetings = true;
    this.leaderService.getScheduledMeetings('upcoming').subscribe({
      next: (res: any) => {
        this.isLoadingMeetings = false;
        const meetings: Meeting[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.meetingDetails)
            ? res.data.meetingDetails
            : [];

        const sortedMeetings = [...meetings].sort((firstMeeting, secondMeeting) => {
          return this.getMeetingStartDateTime(firstMeeting).getTime() - this.getMeetingStartDateTime(secondMeeting).getTime();
        });

        const now = new Date();
        const activeOrUpcomingMeetings = sortedMeetings.filter((meeting) => {
          return this.getMeetingEndDateTime(meeting).getTime() >= now.getTime();
        });

        const meetingsToDisplay = activeOrUpcomingMeetings.length ? activeOrUpcomingMeetings : sortedMeetings;

        this.allMeetings = meetingsToDisplay;
        this.nextMeeting = meetingsToDisplay.length ? meetingsToDisplay[0] : null;
        this.remainingMeetings = meetingsToDisplay.slice(1, 4);
      },
      error: (err) => {
        this.isLoadingMeetings = false;
        console.error('Error loading meetings:', err);
        this.allMeetings = [];
        this.nextMeeting = null;
        this.remainingMeetings = [];
      }
    });
  }

  joinMeeting(url: string): void {
    if (url) window.open(url, '_blank');
  }

  openHospitalEditerPage(hospitalId: string) {
    this.router.navigate(['/leader-toolkit-hospital-editor', hospitalId]);
  }

  openHospitalOpsDashboard(hospitalId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.router.navigate(
      ['/leader-tookit-hospitals', hospitalId, 'ops-dashboard'],
      {
        queryParams: {
          quarter: this.getResolvedQuarter(this.selectedQuarter)
        }
      }
    );
  }

  openAddMeetingPage() {
    this.router.navigate(['/leader-toolkit-scheduling']);
  }

  openMeetingDetailDialog(row: any): void {
    this.dialog.open(MeetingDetailDialogComponent, {
      width: '500px',
      data: row
    });
  }

openViewTask(taskData: any, page: 'tasks' | 'goals' = 'tasks') {
     const isMultiTaskPayload = Array.isArray(taskData);
     const dialogRef = this.dialog.open(ViewTaskModalComponent, {
       data: {
         taskId: taskData,
         page: isMultiTaskPayload ? 'goals' : page,
         quarter: this.getResolvedQuarter(this.selectedQuarter)
       },
      width: '600px',
      disableClose: true,
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.applyQtr(this.quarterList);
      }
    })
  }

  taskListPage() {
    this.router.navigate(['/leader-toolkit-tasks']);
  }


  getTotalAssignedTaskCount(goals: any[]): number {
    if (!goals || !goals.length) return 0;
    return goals.reduce((sum, g) => sum + (g.taskCount || 0), 0);
  }

  getTotalCompletedTaskCount(goals: any[]): number {
    if (!goals || !goals.length) return 0;
    return goals.reduce((sum, g) => sum + (g.taskComplete || 0), 0);
  }

  getTotalStaleGoals(goals: any[]): number {
    if (!goals || !goals.length) return 0;
    return goals.reduce((sum, g) => sum + (Number(g?.staleGoals) || 0), 0);
  }

  getTotalPendingGoals(goals: any[]): number {
    if (!goals || !goals.length) return 0;
    return goals.reduce((sum, g) => sum + (Number(g?.pendingGoals) || 0), 0);
  }

  getHospitalTaskInfos(goals: any[]): any[] {
    if (!Array.isArray(goals) || !goals.length) return [];

    const allTaskInfos = goals.flatMap((goal: any) =>
      Array.isArray(goal?.taskInfos) ? goal.taskInfos : []
    );

    const uniqueByTaskId = new Map<number, any>();
    allTaskInfos.forEach((task: any) => {
      const taskId = Number(task?.taskId || 0);
      if (taskId > 0 && !uniqueByTaskId.has(taskId)) {
        uniqueByTaskId.set(taskId, task);
      }
    });

    return Array.from(uniqueByTaskId.values());
  }



  getMomentumClass(momentum: string): string {
    if (!momentum) return '';
    const m = momentum.toString().toUpperCase();
    if (m === 'IMPROVING') return 'improving';
    if (m === 'DECLINING') return 'declining';
    if (m === 'FLAT') return 'flat';
    return '';
  }

  getMomentumIcon(momentum: string): string {
    if (!momentum) return 'remove';
    const m = momentum.toString().toUpperCase();
    if (m === 'DECLINING') return 'arrow_downward';
    if (m === 'FLAT') return 'remove';
    return 'arrow_upward';
  }

  getMomentumLabel(momentum: string): string {
    if (!momentum) return '—';
    const m = momentum.toString().toUpperCase();
    if (m === 'DECLINING') return 'Declining';
    if (m === 'FLAT') return 'Flat';
    return 'Improving';
  }

  isOnTrackStatus(status: string): boolean {
    const normalized = (status || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    return normalized === 'ON_TRACK';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ON_TRACK': return 'On Track';
      case 'At_Risk': return 'At Risk';
      case 'Off_Track': return 'Off Track';
      default: return status;
    }
  }

  getExecutionHealthClass(executionHealth: string): string {
    if (!executionHealth) return '';
    const value = executionHealth.toString().toUpperCase().replace(/[_-]+/g, ' ');
    if (value.includes('ACTIVE') || value.includes('GREEN')) return 'active';
    if (value.includes('SLOWING') || value.includes('YELLOW')) return 'slowing';
    if (value.includes('STALLED') || value.includes('RED')) return 'stalled';
    return '';
  }

  getExecutionHealthLabel(executionHealth: string): string {
    if (!executionHealth) return '-';
    const value = executionHealth.toString().toUpperCase().replace(/[_-]+/g, ' ');
    if (value.includes('ACTIVE') || value.includes('GREEN')) return 'ACTIVE';
    if (value.includes('SLOWING') || value.includes('YELLOW')) return 'SLOWING';
    if (value.includes('STALLED') || value.includes('RED')) return 'STALLED';
    return value;
  }

  getRmAttentionRequiredLabel(value: string): string {
    const normalized = (value || '').toString().trim().toUpperCase();
    if (normalized === 'ACTION REQUIRED') return 'Action Required';
    if (normalized === 'MONITOR') return 'Monitor';
    if (normalized === 'NO ACTION NEEDED') return 'No Action Needed';
    return '-';
  }

  getRmAttentionRequiredClass(value: string): string {
    const normalized = this.getRmAttentionRequiredLabel(value).toUpperCase();
    if (normalized === 'ACTION REQUIRED') return 'action-required';
    if (normalized === 'MONITOR') return 'monitor';
    if (normalized === 'NO ACTION NEEDED') return 'no-action-needed';
    return '';
  }

  getIssueDriverLabel(value: string | null | undefined): string {
    const normalized = (value || '').toString().trim().toUpperCase();
    if (normalized === 'LEADERSHIP BOTTLENECK') return 'Leadership Bottleneck';
    if (normalized === 'EXECUTION FAILURE') return 'Execution Failure';
    if (normalized === 'PERFORMANCE DECLINE') return 'Performance Decline';
    return '-';
  }

  getIssueDriverClass(value: string | null | undefined): string {
    const normalized = (value || '').toString().trim().toUpperCase();
    if (normalized === 'LEADERSHIP BOTTLENECK') return 'leadership-bottleneck';
    if (normalized === 'EXECUTION FAILURE') return 'execution-failure';
    if (normalized === 'PERFORMANCE DECLINE') return 'performance-decline';
    return '';
  }


  locationBack(): void {
    this.location.back();
  }

  getHospitalList(): void {
    this.leaderService.getHospitalList().subscribe((res: any) => {
      this.hospitalList = res?.data || [];
    });
  }

  newAddMeetingPage(): void {
    const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
      width: '850px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: this.hospitalList,
      disableClose: false
    })
    dialogRef.afterClosed().subscribe(result => {
      console.log('Meeting dialog closed with result:', result);

      const shouldRefreshMeetings =
        result === 'success' ||
        result?.status === 'success' ||
        result?.refreshMeetings === true;

      if (shouldRefreshMeetings) {
        this.loadTodayMeetings();
      }
    })
  }

  createNewtask() {
    const dialogRef = this.dialog.open(CreateNewTaskComponent, {
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result == 'success') {
        this.applyQtr(this.quarterList);
      }
    });
  }

  openCreateTask(goal?: any, hospital?: any): void {
    const selectedQuarterName = this.getResolvedQuarter(this.selectedQuarter);
    const selectedQuarter = (this.quarterList || []).find((q: any) => q?.quarterName === selectedQuarterName) || null;

    const dialogRef = this.dialog.open(CreateNewTaskComponent, {
      data: {
        hospital: hospital || null,
        quarter: selectedQuarter,
        goal: goal
          ? {
              goalTargetId: goal.goalTargetId ?? null,
              goalName: goal.goalName ?? null,
            }
          : null,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'success') {
        this.applyQtr(this.quarterList);
      }
    });
  }

  viewGoalPage(row: any) {
    const dialogRef = this.dialog.open(GoalDetailsComponent, {
      data: row.goalTargetId
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result == 'success') {
      }
    });
  }
}
